import {IUser} from '../auth/interfaces';
import axios, {AxiosError, InternalAxiosRequestConfig} from 'axios';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

const isNetworkError = (error: AxiosError): boolean => {
  return !error.response && Boolean(error.code && ['ENOTFOUND', 'ECONNABORTED', 'ECONNRESET', 'ECONNREFUSED'].includes(error.code));
};

const isRetryableError = (error: AxiosError): boolean => {
  if (isNetworkError(error)) return true;
  if (error.response?.status && [408, 429, 500, 502, 503, 504].includes(error.response.status)) return true;

  return false;
};

const delay = (ms: number): Promise<void> => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

axios.defaults.timeout = 30000;

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
}

axios.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    const newConfig = {...config};

    if (!newConfig.metadata) {
      newConfig.metadata = {startTime: new Date()};
    }

    return newConfig;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & {
      metadata?: {startTime: Date; retryCount?: number}
    };

    if (!config.metadata) {
      config.metadata = {startTime: new Date()};
    }

    if (!config.metadata.retryCount) {
      config.metadata.retryCount = 0;
    }

    if (isRetryableError(error) && config.metadata.retryCount < MAX_RETRIES) {
      config.metadata.retryCount += 1;
      const retryDelay = INITIAL_RETRY_DELAY * (2 ** (config.metadata.retryCount - 1));

      console.warn(`Request failed, retrying in ${retryDelay}ms (attempt ${config.metadata.retryCount}/${MAX_RETRIES})`, {
        url: config.url,
        method: config.method,
        error: error.message,
        status: error.response?.status
      });

      await delay(retryDelay);

      return axios.request(config);
    }

    if (isNetworkError(error)) {
      const enhancedError = new Error('Network connection failed. Please check your internet connection and try again.');

      enhancedError.name = 'NetworkError';
      throw enhancedError;
    }

    throw error;
  }
);

export function setAxiosHeaders(user: IUser, token: string): void {
  axios.defaults.headers.common.Authorization = token;
  axios.defaults.headers.common.API_KEY = process.env.REACT_APP_API_KEY;
  axios.defaults.headers.common['Content-Type'] = 'application/json';
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  if (user && user['https://clever.com']) {
    axios.defaults.headers.common.CLEVER_TOKEN = user['https://clever.com'].cleverToken;
  }
}
